import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, useGameStatus, useIsConnected, useIsReconnecting, useError } from './stores/gameStore';
import { useSocket } from './hooks/useSocket';

// View components
import LandingView from './components/LandingView';
import LobbyView from './components/LobbyView';
import CategoryInputView from './components/CategoryInputView';
import NicheAnalysisView from './components/NicheAnalysisView';
import VotingView from './components/VotingView';
import GeneratingView from './components/GeneratingView';
import GameLoopView from './components/GameLoopView';
import ResultView from './components/ResultView';
import ScoreboardView from './components/ScoreboardView';
import PenaltyView from './components/PenaltyView';

// Animated background bubbles
const BubbleOverlay = () => (
  <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        initial={{ y: '110vh', x: `${Math.random() * 100}vw`, opacity: 0 }}
        animate={{
          y: '-10vh',
          opacity: [0, 0.4, 0],
          x: `${Math.random() * 100}vw`
        }}
        transition={{
          duration: 5 + Math.random() * 10,
          repeat: Infinity,
          delay: Math.random() * 10,
          ease: 'linear'
        }}
        className="absolute w-2 h-2 rounded-full bg-white/20 blur-[1px]"
      />
    ))}
  </div>
);

// Connection status indicator
const ConnectionIndicator = () => {
  const isConnected = useIsConnected();
  const isReconnecting = useIsReconnecting();
  const error = useError();

  if (isConnected && !isReconnecting && !error) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        className="fixed top-6 left-4 right-4 z-[200] flex justify-center"
      >
        <div
          className={`glass px-6 py-3 rounded-2xl border-2 flex items-center gap-3 shadow-xl ${
            isReconnecting
              ? 'border-yellow-500/50 bg-yellow-950/40 text-yellow-500 shadow-yellow-500/10'
              : 'border-red-500/50 bg-red-950/40 text-red-500 shadow-red-500/10'
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full animate-pulse ${
              isReconnecting ? 'bg-yellow-500' : 'bg-red-500'
            }`}
          />
          <span className="font-black text-xs uppercase tracking-widest">
            {isReconnecting ? 'Reconnecting...' : error || 'Connection Lost'}
          </span>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Loading spinner
const LoadingSpinner = () => (
  <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 bg-[#0a0a0a] text-center">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      className="w-16 h-16 border-4 border-t-neon-purple border-white/10 rounded-full mb-4"
    />
    <h2 className="text-2xl font-black italic uppercase text-neon-purple animate-pulse">
      Connecting...
    </h2>
    <p className="text-white/40 font-bold uppercase text-[10px] mt-2 tracking-widest">
      Establishing connection to server
    </p>
  </div>
);

const App = () => {
  const status = useGameStatus();
  const isConnected = useIsConnected();
  const { gameState, clientState } = useGameStore();

  // Initialize socket connection
  useSocket();

  // Show loading while connecting initially
  if (!isConnected && status === 'LANDING') {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center p-4 bg-[#0a0a0a] overflow-y-auto relative">
      <BubbleOverlay />
      <ConnectionIndicator />

      <div className="relative z-10 w-full flex flex-col items-center justify-center min-h-full">
        {(() => {
          switch (status) {
            case 'LANDING': return <LandingView key="landing" />;
            case 'LOBBY': return <LobbyView key="lobby" />;
            case 'CATEGORY_INPUT': return <CategoryInputView key="category" />;
            case 'NICHE_ANALYSIS': return <NicheAnalysisView key="niche" />;
            case 'VOTING': return <VotingView key="voting" />;
            case 'GENERATING':
            case 'QUIZ_READY': return <GeneratingView key="generating" />;
            case 'GAME_LOOP': return <GameLoopView key="gameloop" />;
            case 'ROUND_RESULT': return <ResultView key="result" />;
            case 'SCOREBOARD': return <ScoreboardView key="scoreboard" />;
            case 'GAME_OVER_PENALTY': return <PenaltyView key="penalty" />;
            default: return null;
          }
        })()}
      </div>

      {/* Debug info */}
      {gameState.roomCode && (
        <div className="fixed bottom-4 left-4 text-[8px] text-white/10 font-mono pointer-events-none uppercase tracking-widest z-50">
          Room: {gameState.roomCode} | ID: {clientState.myId?.slice(0, 8)} | Status:{' '}
          {isConnected ? 'ONLINE' : 'OFFLINE'}
        </div>
      )}
    </div>
  );
};

export default App;
